/**
 * Pure, deterministic contour-comparison metrics over two binary
 * segmentation masks that share ONE voxel grid.
 *
 * LIVE client-side metric for the in-viewer contour comparison. The compare
 * panel no longer round-trips these numbers to the backend segmetrics service
 * (that service still powers the separate SegVerify workflow — see
 * useSegmentationMetrics.ts — it is just no longer on the compare-panel path).
 * Instead the panel's client `useContourComparisonMetrics` hook reads each
 * ROI's SDF texture back to a binary mask, aligns the pair onto one grid (see
 * `contourMaskAlignment`), and calls {@link compareContours}. These numbers ARE
 * the device's reported DSC / HD95 / MSD — safety-relevant clinical output, not
 * a preview.
 *
 * Because it is now the authoritative result, it needs clinical / V&V sign-off:
 * the unweighted voxel-surface convention below differs from the old backend's
 * area-weighting, so values shift versus the previously-shown backend numbers.
 *
 * Domain layer (CLAUDE.md): no GPU, no DOM, no framework. All distances are
 * in **mm** and volumes in **mm³** (the internal domain units); conversion to
 * display units happens at the UI boundary. Inputs are already-binarised
 * masks — thresholding a signed-distance field into a mask is an engine-layer
 * concern and is kept out of here so the math is testable in isolation.
 *
 * Metrics provided (the three shown in the compare panel):
 *  - {@link diceCoefficient}: volumetric Dice similarity, unitless [0, 1].
 *  - HD95: 95th-percentile symmetric surface (Hausdorff) distance, mm.
 *  - Mean surface distance: symmetric mean surface distance, mm.
 *
 * Surface-distance convention (see {@link compareContours}): surfaces are the
 * set of foreground voxel CENTRES that touch background under 6-connectivity,
 * mapped to mm by the per-axis spacing; distances are point-to-nearest-point
 * between the two surfaces, taken symmetrically (both directions pooled). This
 * is the unweighted voxel-surface convention — chosen for determinism and
 * testability. It differs from the removed backend, which area-weighted surface
 * elements (DeepMind `surface-distance` style); the two do NOT agree exactly,
 * which is the crux of the pending clinical / V&V sign-off for this
 * now-authoritative metric.
 */

/** Voxel grid: counts along each axis and physical spacing in mm. */
export interface MaskGrid {
    readonly sizeX: number;
    readonly sizeY: number;
    readonly sizeZ: number;
    /** Voxel spacing in mm along x, y, z. */
    readonly spacingMm: readonly [number, number, number];
}

/**
 * A binary segmentation mask over {@link grid}. `voxels` holds one entry per
 * voxel (non-zero = foreground), indexed `x + sizeX * (y + sizeY * z)`.
 */
export interface BinaryMask {
    readonly grid: MaskGrid;
    readonly voxels: Uint8Array;
}

/**
 * The comparison metrics, in domain units (mm, mm³), as returned by
 * {@link compareContours}. These are formatted directly for display — no
 * backend `SegmentationData` DTO is produced. The compare panel's
 * `comparisonMetricRows` (fed by the `useContourComparisonMetrics` hook) maps
 * three of these fields to the shown rows, applying the display precision/units
 * from docs/ui_specs.md at the UI boundary:
 *   dice                  → DSC   (unitless, 3 dp)
 *   hd95Mm                → HD95  (mm, 1 dp)
 *   meanSurfaceDistanceMm → MSD   (mm, 2 dp)
 * The remaining fields (hausdorffMaxMm, referenceVolumeMm3, testVolumeMm3) are
 * computed for completeness and testing but are not currently displayed. The
 * backend `mean_hd` / `gt_vol_mm3` / `user_vol_mm3` shape belongs to the
 * separate SegVerify workflow and is not derived from this type.
 */
export interface ContourComparisonMetrics {
    /** Volumetric Dice similarity coefficient, unitless [0, 1]. */
    readonly dice: number;
    /** 95th-percentile symmetric surface distance, mm. */
    readonly hd95Mm: number;
    /** Symmetric mean surface distance, mm. */
    readonly meanSurfaceDistanceMm: number;
    /** Maximum symmetric surface distance (full Hausdorff), mm. */
    readonly hausdorffMaxMm: number;
    /** Binary volume of the reference mask, mm³. */
    readonly referenceVolumeMm3: number;
    /** Binary volume of the test mask, mm³. */
    readonly testVolumeMm3: number;
}

/** Thrown for invalid inputs (mismatched grids, empty surfaces). */
export class ContourComparisonError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = 'ContourComparisonError';
    }
}

function voxelTotal(grid: MaskGrid): number {
    return grid.sizeX * grid.sizeY * grid.sizeZ;
}

function assertSameGrid(a: MaskGrid, b: MaskGrid): void {
    if (a.sizeX !== b.sizeX || a.sizeY !== b.sizeY || a.sizeZ !== b.sizeZ) {
        throw new ContourComparisonError(
            'Masks must share voxel dimensions: ' +
                `${a.sizeX}x${a.sizeY}x${a.sizeZ} vs ` +
                `${b.sizeX}x${b.sizeY}x${b.sizeZ}.`
        );
    }
    for (let i = 0; i < 3; i += 1) {
        if (a.spacingMm[i] !== b.spacingMm[i]) {
            throw new ContourComparisonError(
                'Masks must share voxel spacing (mm).'
            );
        }
    }
}

function assertMaskShape(mask: BinaryMask): void {
    const expected = voxelTotal(mask.grid);
    if (mask.voxels.length !== expected) {
        throw new ContourComparisonError(
            `Mask voxel array length ${mask.voxels.length} does not match ` +
                `grid volume ${expected}.`
        );
    }
}

interface MaskCounts {
    /** Foreground voxel count of mask A. */
    readonly countA: number;
    /** Foreground voxel count of mask B. */
    readonly countB: number;
    /** Voxels that are foreground in both A and B. */
    readonly intersection: number;
}

/**
 * Both masks' foreground counts and their intersection, in a SINGLE pass over
 * the shared voxel array. These three counts feed both Dice and the binary
 * volumes, so computing them together avoids rescanning millions of voxels.
 * Callers must validate the masks (shape + grid) first.
 */
function countMasks(a: BinaryMask, b: BinaryMask): MaskCounts {
    const av = a.voxels;
    const bv = b.voxels;
    let countA = 0;
    let countB = 0;
    let intersection = 0;
    for (let i = 0; i < av.length; i += 1) {
        const inA = av[i] !== 0;
        const inB = bv[i] !== 0;
        if (inA) {
            countA += 1;
        }
        if (inB) {
            countB += 1;
        }
        if (inA && inB) {
            intersection += 1;
        }
    }
    return { countA, countB, intersection };
}

/**
 * Volumetric Dice from precomputed counts: `2|A∩B| / (|A| + |B|)`. Two empty
 * masks are defined as perfectly in agreement (returns 1); a non-empty mask
 * against an empty one returns 0.
 */
function diceFromCounts(counts: MaskCounts): number {
    const denominator = counts.countA + counts.countB;
    if (denominator === 0) {
        return 1;
    }
    return (2 * counts.intersection) / denominator;
}

/**
 * Volumetric Dice similarity coefficient over the binary voxel masks. See
 * {@link diceFromCounts} for the degenerate-case rules. Both masks must share
 * dimensions and spacing.
 */
export function diceCoefficient(a: BinaryMask, b: BinaryMask): number {
    assertMaskShape(a);
    assertMaskShape(b);
    assertSameGrid(a.grid, b.grid);
    return diceFromCounts(countMasks(a, b));
}

interface MaskScan {
    readonly counts: MaskCounts;
    /** Reference-mask boundary voxel centres, flat `[x, y, z, ...]` in mm. */
    readonly surfaceAMm: number[];
    /** Test-mask boundary voxel centres, flat `[x, y, z, ...]` in mm. */
    readonly surfaceBMm: number[];
}

/**
 * Single pass over the shared grid producing everything the metrics need: the
 * foreground/intersection counts (for Dice and the binary volumes) and both
 * masks' boundary-voxel centres in mm. Fusing this collapses what was three
 * separate full-volume scans (one for counts, one per surface) into one — the
 * dominant cost on large structures, where the volume has millions of voxels.
 *
 * A foreground voxel is on the surface when a 6-face-neighbour is background or
 * off-grid (so an object touching the grid edge has surface there, matching a
 * background-padded distance transform). Neighbour lookups use index strides
 * (`i ± 1`, `i ± sizeX`, `i ± sliceStride`) rather than recomputing
 * coordinates; the edge tests short-circuit before any out-of-range access.
 * Coordinates are `index * spacing` per axis — the origin and (orthonormal)
 * orientation are omitted because they do not affect inter-surface distances.
 */
function scanMasks(a: BinaryMask, b: BinaryMask): MaskScan {
    const { sizeX, sizeY, sizeZ, spacingMm } = a.grid;
    const [sx, sy, sz] = spacingMm;
    const av = a.voxels;
    const bv = b.voxels;
    const sliceStride = sizeX * sizeY;
    const lastX = sizeX - 1;
    const lastY = sizeY - 1;
    const lastZ = sizeZ - 1;

    const onBoundary = (
        v: Uint8Array,
        i: number,
        x: number,
        y: number,
        z: number
    ): boolean =>
        x === 0 ||
        x === lastX ||
        y === 0 ||
        y === lastY ||
        z === 0 ||
        z === lastZ ||
        v[i - 1] === 0 ||
        v[i + 1] === 0 ||
        v[i - sizeX] === 0 ||
        v[i + sizeX] === 0 ||
        v[i - sliceStride] === 0 ||
        v[i + sliceStride] === 0;

    let countA = 0;
    let countB = 0;
    let intersection = 0;
    const surfaceAMm: number[] = [];
    const surfaceBMm: number[] = [];

    let i = 0;
    for (let z = 0; z < sizeZ; z += 1) {
        for (let y = 0; y < sizeY; y += 1) {
            for (let x = 0; x < sizeX; x += 1, i += 1) {
                const inA = av[i] !== 0;
                const inB = bv[i] !== 0;
                if (inA) {
                    countA += 1;
                    if (onBoundary(av, i, x, y, z)) {
                        surfaceAMm.push(x * sx, y * sy, z * sz);
                    }
                }
                if (inB) {
                    countB += 1;
                    if (onBoundary(bv, i, x, y, z)) {
                        surfaceBMm.push(x * sx, y * sy, z * sz);
                    }
                    if (inA) {
                        intersection += 1;
                    }
                }
            }
        }
    }

    return { counts: { countA, countB, intersection }, surfaceAMm, surfaceBMm };
}

/**
 * A balanced 3D k-d tree over a flat point buffer, supporting nearest-point
 * squared-distance queries. Built once per surface and reused for every query
 * from the opposing surface, turning the O(n·m) brute-force surface-distance
 * computation into roughly O((n + m)·log m).
 */
class KdTree3 {
    /** Point index stored at each node, in heap-less median-split order. */
    private readonly nodeIndex: Int32Array;
    /** Source coordinates, flat `[x, y, z, ...]`. */
    private readonly coords: readonly number[];
    private readonly pointCount: number;
    /**
     * Reusable traversal stack (frames of lo, hi, axis, gateSq), so a nearest
     * query allocates nothing. The query runs once per surface voxel, so a
     * per-query closure or stack would dominate cost on large structures.
     */
    private readonly stack: number[] = [];

    public constructor(coords: readonly number[]) {
        this.coords = coords;
        this.pointCount = coords.length / 3;
        const order = new Int32Array(this.pointCount);
        for (let i = 0; i < this.pointCount; i += 1) {
            order[i] = i;
        }
        this.nodeIndex = order;
        this.build(0, this.pointCount, 0);
    }

    public get isEmpty(): boolean {
        return this.pointCount === 0;
    }

    /**
     * Squared distance from `(x, y, z)` to the nearest stored point. Iterative
     * with an explicit, reused stack so each call allocates nothing.
     *
     * Stack frames are 4 numbers — lo, hi, axis, gateSq. `gateSq < 0` marks an
     * unconditional (near-side) frame; `gateSq >= 0` gates a far-side frame,
     * which is skipped once the best hit is nearer than the splitting plane
     * (the k-d tree pruning step). The near side is pushed last so it pops
     * first and tightens `best` before the far gate is tested — matching the
     * recursive order exactly.
     */
    public nearestDistanceSq(x: number, y: number, z: number): number {
        const { coords, nodeIndex, stack } = this;
        let best = Number.POSITIVE_INFINITY;
        let sp = 0;
        stack[sp++] = 0;
        stack[sp++] = this.pointCount;
        stack[sp++] = 0;
        stack[sp++] = -1;
        while (sp > 0) {
            const gateSq = stack[--sp];
            const axis = stack[--sp];
            const hi = stack[--sp];
            const lo = stack[--sp];
            if (lo >= hi || (gateSq >= 0 && gateSq >= best)) {
                continue;
            }
            const mid = (lo + hi) >> 1;
            const pi = nodeIndex[mid] * 3;
            const dx = x - coords[pi];
            const dy = y - coords[pi + 1];
            const dz = z - coords[pi + 2];
            const distSq = dx * dx + dy * dy + dz * dz;
            if (distSq < best) {
                best = distSq;
            }
            const delta = axis === 0 ? dx : axis === 1 ? dy : dz;
            const nextAxis = axis === 2 ? 0 : axis + 1;
            const deltaSq = delta * delta;
            // Push far side first, near side last (LIFO pops near first).
            if (delta < 0) {
                stack[sp++] = mid + 1;
                stack[sp++] = hi;
                stack[sp++] = nextAxis;
                stack[sp++] = deltaSq;
                stack[sp++] = lo;
                stack[sp++] = mid;
                stack[sp++] = nextAxis;
                stack[sp++] = -1;
            } else {
                stack[sp++] = lo;
                stack[sp++] = mid;
                stack[sp++] = nextAxis;
                stack[sp++] = deltaSq;
                stack[sp++] = mid + 1;
                stack[sp++] = hi;
                stack[sp++] = nextAxis;
                stack[sp++] = -1;
            }
        }
        return best;
    }

    /** Median-split the node range on `axis`, recursing into both halves. */
    private build(lo: number, hi: number, axis: number): void {
        if (hi - lo <= 1) {
            return;
        }
        const mid = (lo + hi) >> 1;
        this.nthElement(lo, hi, mid, axis);
        const nextAxis = (axis + 1) % 3;
        this.build(lo, mid, nextAxis);
        this.build(mid + 1, hi, nextAxis);
    }

    /** Partition `nodeIndex[lo, hi)` so the `k`th element is in sorted place. */
    private nthElement(lo: number, hi: number, k: number, axis: number): void {
        let left = lo;
        let right = hi - 1;
        while (left < right) {
            const pivot = this.coordAt(
                this.nodeIndex[(left + right) >> 1],
                axis
            );
            let i = left;
            let j = right;
            while (i <= j) {
                while (this.coordAt(this.nodeIndex[i], axis) < pivot) {
                    i += 1;
                }
                while (this.coordAt(this.nodeIndex[j], axis) > pivot) {
                    j -= 1;
                }
                if (i <= j) {
                    const tmp = this.nodeIndex[i];
                    this.nodeIndex[i] = this.nodeIndex[j];
                    this.nodeIndex[j] = tmp;
                    i += 1;
                    j -= 1;
                }
            }
            if (k <= j) {
                right = j;
            } else if (k >= i) {
                left = i;
            } else {
                break;
            }
        }
    }

    private coordAt(pointIndex: number, axis: number): number {
        return this.coords[pointIndex * 3 + axis];
    }
}

/** Directed nearest-surface distances (mm) from each point in `from` to `tree`. */
function directedDistancesMm(
    from: readonly number[],
    tree: KdTree3
): Float64Array {
    const count = from.length / 3;
    const out = new Float64Array(count);
    for (let i = 0; i < count; i += 1) {
        const p = i * 3;
        out[i] = Math.sqrt(
            tree.nearestDistanceSq(from[p], from[p + 1], from[p + 2])
        );
    }
    return out;
}

/**
 * Linear-interpolated percentile (NumPy's default method) of `values` at
 * `fraction` in [0, 1]. `values` is not mutated. Matches the convention a
 * NumPy-based backend would use for HD95 so the definitions can be compared
 * directly.
 */
export function percentile(values: Float64Array, fraction: number): number {
    if (values.length === 0) {
        throw new ContourComparisonError('percentile of an empty set.');
    }
    const sorted = Float64Array.from(values).sort();
    if (sorted.length === 1) {
        return sorted[0];
    }
    const rank = fraction * (sorted.length - 1);
    const lo = Math.floor(rank);
    const hi = Math.ceil(rank);
    if (lo === hi) {
        return sorted[lo];
    }
    return sorted[lo] + (rank - lo) * (sorted[hi] - sorted[lo]);
}

/** Concatenate two distance buffers into one (for symmetric pooling). */
function concat(a: Float64Array, b: Float64Array): Float64Array {
    const out = new Float64Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
}

/**
 * Compute all three comparison metrics for a reference and a test mask sharing
 * one voxel grid. Surface metrics pool both directed distance sets (reference→
 * test and test→reference) so the result is symmetric.
 *
 * @throws ContourComparisonError if the grids differ, an array length is
 * wrong, or either mask has no surface (empty ROI). Empty-ROI handling is left
 * to the application layer, which surfaces it as "metrics unavailable" rather
 * than reporting a misleading 0 mm distance.
 */
export function compareContours(
    reference: BinaryMask,
    test: BinaryMask
): ContourComparisonMetrics {
    assertMaskShape(reference);
    assertMaskShape(test);
    assertSameGrid(reference.grid, test.grid);

    // One pass over the volume yields Dice, both binary volumes, and both
    // surfaces (validated above, so no re-validation via diceCoefficient).
    const { counts, surfaceAMm, surfaceBMm } = scanMasks(reference, test);
    if (surfaceAMm.length === 0 || surfaceBMm.length === 0) {
        throw new ContourComparisonError(
            'Surface distance is undefined for an empty ROI.'
        );
    }

    const referenceTree = new KdTree3(surfaceAMm);
    const testTree = new KdTree3(surfaceBMm);
    const refToTest = directedDistancesMm(surfaceAMm, testTree);
    const testToRef = directedDistancesMm(surfaceBMm, referenceTree);
    const pooled = concat(refToTest, testToRef);

    // Mean and max surface distance share one pass over the pooled distances.
    let distanceSumMm = 0;
    let hausdorffMaxMm = 0;
    for (let i = 0; i < pooled.length; i += 1) {
        const d = pooled[i];
        distanceSumMm += d;
        if (d > hausdorffMaxMm) {
            hausdorffMaxMm = d;
        }
    }

    const [sx, sy, sz] = reference.grid.spacingMm;
    const voxelVolumeMm3 = sx * sy * sz;

    return {
        dice: diceFromCounts(counts),
        hd95Mm: percentile(pooled, 0.95),
        meanSurfaceDistanceMm: distanceSumMm / pooled.length,
        hausdorffMaxMm,
        referenceVolumeMm3: counts.countA * voxelVolumeMm3,
        testVolumeMm3: counts.countB * voxelVolumeMm3,
    };
}
